import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Check } from "lucide-react";

const stats = [
  { v: "10+", k: "Years in Vending" },
  { v: "Pan-India", k: "Service & Spares" },
  { v: "98%", k: "Uptime SLA" },
];

const usps = [
  "Own the machine outright — no monthly rentals",
  "AMC plans for hassle-free service",
  "Cashless, IoT-ready, live on day one",
];

export default function Hero({ onOpenInquiry }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-white"
    >
      <div className="absolute inset-0 bg-dot-grid-light opacity-70 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] uppercase tracking-[0.2em] text-emerald-700"
          >
            <Sparkles className="w-3.5 h-3.5" />
            India&apos;s Smart Vending Partner
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-950 leading-[0.95]"
          >
            Buy a vending
            <br />
            machine that
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">pays you back</span>
              <span className="absolute left-0 right-0 -bottom-2 h-3 bg-emerald-300 rounded-md -z-0" />
            </span>
            <span className="text-amber-500">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-7 max-w-xl text-zinc-600 text-base md:text-lg leading-relaxed"
          >
            Snacks vending machines, beverage vending machines, PPE and stationery
            dispensers — engineered for Indian offices, factories, hospitals and
            retail. Own the asset. We deliver, install and maintain.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 space-y-2"
          >
            {usps.map((u) => (
              <li key={u} className="flex items-start gap-3 text-sm md:text-[15px] text-zinc-700">
                <span className="mt-1 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                {u}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={onOpenInquiry}
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-7 py-4 transition shadow-[0_10px_30px_rgba(16,185,129,0.35)]"
            >
              Get a Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <a
              data-testid="hero-cta-explore"
              href="#machines"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("machines")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-900 rounded-full px-7 py-4 transition"
            >
              Explore Machines
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 grid grid-cols-3 max-w-md gap-6"
          >
            {stats.map((s) => (
              <div key={s.k}>
                <div className="font-display text-2xl md:text-3xl font-bold text-zinc-950 tracking-tight">
                  {s.v}
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                  {s.k}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Visual: AI 3D product render with floating clip-art accents */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          {/* Floating clip-art badges */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="hidden md:flex absolute -top-6 -left-6 z-20 items-center gap-2 px-3.5 py-2 rounded-2xl bg-white border border-zinc-200 shadow-xl rotate-[-4deg]"
          >
            <span className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[11px] font-bold">
              ₹
            </span>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Pay via
              </div>
              <div className="text-sm text-zinc-950 font-semibold">UPI / QR / Card</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 8, y: 8 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.6, delay: 0.75 }}
            className="hidden md:flex absolute -top-4 -right-6 z-20 items-center gap-2 px-3.5 py-2 rounded-2xl bg-zinc-950 text-white shadow-xl rotate-[3deg]"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">
                IoT
              </div>
              <div className="text-sm font-semibold">Live telemetry</div>
            </div>
          </motion.div>

          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-zinc-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 shadow-2xl shadow-zinc-900/10">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/refuel_core_hero.png`}
              alt="Refuel CORE vending machine — Indian brands inside"
              className="absolute inset-0 w-full h-full object-cover"
            />

            <div className="absolute left-5 top-5 right-5 flex items-center justify-between">
              <span className="px-3 py-1 rounded-full bg-zinc-950 text-white text-[10px] font-bold uppercase tracking-[0.2em] shadow">
                For Sale
              </span>
              <span className="px-3 py-1 rounded-full bg-white/90 backdrop-blur border border-white text-zinc-800 text-[10px] uppercase tracking-[0.2em] shadow">
                Refuel · CORE
              </span>
            </div>

            <div className="absolute bottom-5 left-5 right-5">
              <div className="rounded-2xl bg-white/85 backdrop-blur-md border border-white shadow-lg p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 mb-1">
                  Featured Machine
                </div>
                <div className="font-display text-lg md:text-xl font-semibold text-zinc-950 tracking-tight">
                  300+ items · Cashless · Cloud-monitored
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-white border border-zinc-200 px-2 py-2 text-center">
                    <div className="text-[10px] text-zinc-500">Display</div>
                    <div className="text-xs text-zinc-950 font-semibold">HD Touch</div>
                  </div>
                  <div className="rounded-xl bg-white border border-zinc-200 px-2 py-2 text-center">
                    <div className="text-[10px] text-zinc-500">Pay</div>
                    <div className="text-xs text-zinc-950 font-semibold">UPI / Card</div>
                  </div>
                  <div className="rounded-xl bg-white border border-zinc-200 px-2 py-2 text-center">
                    <div className="text-[10px] text-zinc-500">Cooling</div>
                    <div className="text-xs text-zinc-950 font-semibold">Precision</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="absolute -bottom-6 -left-4 sm:flex hidden items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-zinc-200 shadow-xl"
          >
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Remote Telemetry
              </div>
              <div className="text-sm text-zinc-950 font-semibold">Stock & Health · Realtime</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
